import { type DatabaseProperties } from ":shared/database/database-properties";
import Dexie, { type IndexableType, type Table } from "dexie";
import { log } from ":shared/lib/logger";

// Tables to exclude from detailed logging to avoid infinite loops
const EXCLUDED_TABLES = ["debug_logs"];

export class Database extends Dexie {
	// Core tables
	german_words!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;
	cards!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;
	card_review_logs!: Table<
		DatabaseProperties,
		IndexableType,
		DatabaseProperties
	>;
	tags!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;
	decks!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;
	translations!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;

	// Association tables (feature-level relations) + external cache
	card_german_words!: Table<
		DatabaseProperties,
		IndexableType,
		DatabaseProperties
	>;
	german_word_tags!: Table<
		DatabaseProperties,
		IndexableType,
		DatabaseProperties
	>;
	deck_cards!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;
	tag_decks!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;

	// Debug and monitoring tables
	debug_logs!: Table<DatabaseProperties, IndexableType, DatabaseProperties>;

	constructor(...params: ConstructorParameters<typeof Dexie>) {
		super(...params);

		log.info("Initializing database", { name: params[0] }, "database");

		this.version(1).stores({
			// core Tables
			german_words: "&id, type, word, [word+type], definitions",
			cards: "&id, state",
			card_review_logs: "&id, cardId",
			tags: "&id, label",
			decks: "&id",
			translations:
				"&id, germanWordId, targetLanguage, [germanWordId+targetLanguage]",

			// Association tables (feature-level relations) + external cache
			card_german_words: "&id, germanWordId, cardId, [cardId+germanWordId]",
			german_word_tags: "&id, germanWordId, tagId, [germanWordId+tagId]",
			deck_cards: "&id, deckId, cardId, [deckId+cardId]",
			tag_decks: "&id, tagId, deckId, [tagId+deckId]",

			// Debug and monitoring tables
			debug_logs: "&id, timestamp, level, context",
		});

		this.on("ready", () => {
			log.success(
				"Database ready",
				{ name: this.name, version: this.verno },
				"database",
			);
		});

		this.on("populate", () => {
			log.info("Database population triggered", undefined, "database");
		});

		this.on("blocked", () => {
			log.warn("Database blocked by another connection", undefined, "database");
		});

		this.on("versionchange", (event) => {
			log.warn("Database version change detected", { event }, "database");
		});

		// Setup transaction logging hooks after schema is defined
		this.setupTransactionLogging();
		this.setupTransactionTracking();
		this.setupErrorTracking();
	}

	/**
	 * Setup comprehensive database transaction logging
	 * Hooks into all CRUD operations across all tables
	 */
	private setupTransactionLogging(): void {
		// Get all table names
		const tableNames = this.tables.map((table) => table.name);

		tableNames.forEach((tableName) => {
			// Skip debug_logs to avoid infinite loop
			if (EXCLUDED_TABLES.includes(tableName)) {
				return;
			}

			const table = this.table(tableName);

			// Hook: Creating (INSERT)
			table.hook("creating", (primKey, obj, _transaction) => {
				log.debug(
					`DB CREATE: ${tableName}`,
					{
						table: tableName,
						operation: "create",
						primaryKey: primKey,
						recordCount: 1,
						data: obj,
					},
					"database",
				);
			});

			// Hook: Reading (SELECT)
			table.hook("reading", (obj) => {
				log.debug(
					`DB READ: ${tableName}`,
					{
						table: tableName,
						operation: "read",
						recordId: obj?.id,
					},
					"database",
				);
				return obj;
			});

			// Hook: Updating (UPDATE)
			table.hook("updating", (modifications, primKey, obj, _transaction) => {
				log.debug(
					`DB UPDATE: ${tableName}`,
					{
						table: tableName,
						operation: "update",
						primaryKey: primKey,
						modifications,
						previousData: obj,
					},
					"database",
				);
			});

			// Hook: Deleting (DELETE)
			table.hook("deleting", (primKey, obj, _transaction) => {
				log.debug(
					`DB DELETE: ${tableName}`,
					{
						table: tableName,
						operation: "delete",
						primaryKey: primKey,
						deletedData: obj,
					},
					"database",
				);
			});
		});

		log.debug(
			"Database CRUD hooks initialized",
			{
				tables: tableNames.filter((t) => !EXCLUDED_TABLES.includes(t)),
			},
			"database",
		);
	}

	/**
	 * Setup transaction lifecycle tracking
	 */
	private setupTransactionTracking(): void {
		// Note: We rely on the hooks above for individual operations
		// Bulk operations will trigger multiple hook calls automatically

		log.debug(
			"Database transaction tracking initialized",
			undefined,
			"database",
		);
	}

	/**
	 * Setup error tracking for database operations
	 */
	private setupErrorTracking(): void {
		// Note: Dexie doesn't have a global 'error' event
		// Errors are caught at the operation level via try/catch in hooks
		// and in the logged-operations wrapper functions

		log.debug("Database error tracking initialized", undefined, "database");
	}

	/**
	 * Execute a transaction with logging
	 */
	async executeTransaction<T>(
		mode: "r" | "rw",
		tables: string[],
		callback: () => Promise<T>,
		context?: string,
	): Promise<T> {
		const transactionId = `txn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

		log.info(
			`Transaction START: ${transactionId}`,
			{
				transactionId,
				mode: mode === "r" ? "readonly" : "readwrite",
				tables,
				context,
			},
			"database",
		);

		try {
			const result = await this.transaction(
				mode,
				tables.map((t) => this.table(t)),
				async () => {
					return await callback();
				},
			);

			log.success(
				`Transaction COMPLETE: ${transactionId}`,
				{
					transactionId,
					tables,
				},
				"database",
			);

			return result;
		} catch (error) {
			log.error(
				`Transaction FAILED: ${transactionId}`,
				{
					transactionId,
					tables,
					error,
				},
				"database",
			);
			throw error;
		}
	}
}
